import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Sstv2MigrationEvidenceRun } from "shared/sstv2MigrationEvidenceRun"
import { evidenceStatusColor } from "./evidenceStatusColor"

const HISTORY_ROW_CAP = 15

export type Sstv2EvidenceRunHistoryProps = {
  /** Newest-first. Only the first `HISTORY_ROW_CAP` are rendered. */
  runs: Sstv2MigrationEvidenceRun[]
  latestRunId?: string
  selectedRunId?: string
  onSelectRun: (runId: string) => void
}

/**
 * Run-history table for the Synthetic Migration tab — click a row to pin that
 * run in the detail view above.
 *
 * Extracted from Sstv2SyntheticMigrationTab to keep that file under the
 * ~400-line component budget once the SL1 locker input landed (LOCKER-344).
 */
export const Sstv2EvidenceRunHistory: FC<Sstv2EvidenceRunHistoryProps> = ({
  runs,
  latestRunId,
  selectedRunId,
  onSelectRun,
}) => {
  const { t } = useTranslation()

  return (
    <Paper data-ui="UI-SL2W-268" variant="outlined" sx={{ p: 2 }}>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>
        {t("evidenceRunHistory")}
      </Typography>
      <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", mb: 1 }}>
        {t("evidenceHistoryHint")}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("run")}</TableCell>
            <TableCell>{t("evidenceStarted")}</TableCell>
            <TableCell>{t("status")}</TableCell>
            <TableCell>
              {t("evidenceIdentified")} / {t("evidenceDelivered")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {runs.slice(0, HISTORY_ROW_CAP).map((r) => (
            <TableRow
              key={r.runId}
              hover
              selected={selectedRunId === r.runId}
              onClick={() => onSelectRun(r.runId)}
              sx={{ cursor: "pointer" }}
            >
              <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                {r.runId.slice(0, 8)}
                {r.runId === latestRunId && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t("latest")}
                    sx={{
                      ml: 0.75,
                      height: 18,
                      "& .MuiChip-label": { px: 0.75, fontSize: "0.62rem" },
                    }}
                  />
                )}
              </TableCell>
              <TableCell sx={{ fontSize: "0.78rem" }}>{r.startedAt}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={evidenceStatusColor(r.status)}
                  label={r.status}
                />
              </TableCell>
              <TableCell sx={{ fontFamily: "monospace", fontSize: "0.78rem" }}>
                {r.verdict
                  ? `${r.verdict.eventsObserved} / ${r.verdict.eventsPublished}`
                  : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}

export default Sstv2EvidenceRunHistory
